/**
 * Reservations Calendar shell (DNKADM-0032 U1; DNKADM-0271 "Now & Next" redesign) —
 * the scheduling surface that **replaces** the old standalone
 * `ReservationsList`/`ReservationsTable`.
 *
 * **The view model since DNKADM-0271 is `Now & Next | Grid`, with Now & Next the
 * DEFAULT landing:** a task-first board (`NowNextView` — arriving/needs-confirmation/
 * later buckets + the coach-load / walk-in rail (DNKADM-0298, replacing the
 * retrospective bay-occupancy strip), built from the loaded day window) for the
 * front desk's minute-to-minute job. **Grid** holds the whole spatial machinery
 * unchanged in capability: Day/Week, the per-view layout toggles, By Bay / By Staff
 * (DNKADM-0041), the three filters, the service legend, and the DNKADM-0271
 * cancelled-collapse toggle (cancelled bookings hide from the grid by default). The
 * shell owns all the state both views read: the selected date, the top view, the
 * grid sub-state, the filters, and the selected booking (the drawer target — shared,
 * so the `?reservation=<uuid>` deeplink opens the drawer from either view).
 *
 * The whole calendar dir is TOKENIZED (DNKADM-0271 §4 — colors via
 * `resolveTokens`/`resolveCalendarMaps`; light byte-identical per the 0266
 * constraint) and the DNKADM-0269 ESLint no-raw-color exemption for this dir is
 * REMOVED — a new raw literal here fails lint.
 *
 * **Service-location scoping (DNKADM-0033 hand-off).** `/admin/reservations` now
 * requires a `serviceLocationId`. The calendar reads the app-level
 * `useServiceLocation()` and **resolves a location before the first fetch**: while
 * the location context is loading it shows the calendar's loading state; in the
 * fail-closed `error` state it shows error + Retry and does **NOT** fetch unscoped.
 * The selected `serviceLocationId` is part of the fetch key, so changing the app-bar
 * location refetches the current window.
 *
 * Filtering is **client-side** over the loaded window (status / service / staff),
 * mirroring the design's `pass(b)`. The staff filter is hidden when NO booking in the
 * window carries a `staff` object (degrade-when-absent). All derivations come from
 * `derive.ts`. The one exception is "Payment needs attention" (DNKADM-0408 U1), which is
 * the server's `paymentAttention` filter on the Grid's window read.
 *
 * No new dependency: native date input + custom MUI layout (no calendar library, no
 * `@mui/x-date-pickers`), per the spec.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Popover,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, inkAlpha, resolveTokens } from '@/theme';
import {
  checkInReservation,
  confirmReservation,
  getReservation,
  listPaymentAttentionReservations,
  listReservations,
  listReservationWindow,
  truncatedWindowMessage,
  overrideAcknowledgements,
  parseOverrideRefusal,
  type ConflictRefusal,
  type ConfirmReservationInput,
  type Reservation,
} from '@/api/reservations';
import { v2ErrorCode } from '@/api/v2Errors';
import { getOperatingHours, type WeeklyHoursDay } from '@/api/operatingHours';
import { dateLabel, dateRangeLabel, longDateLabel } from '@/utils/clockTime';
import { listResourceDateExceptions, listResources } from '@/api/resources';
import { listStaff, listStaffDateExceptions } from '@/api/staff';
import { useResource } from '@/hooks/useResource';
import { parseOption, useListUrlState } from '@/components/list/useListUrlState';
import { useOptionalOperator, useStaffTier } from '@/auth/authHooks';
import { useServiceLocation } from '@/location/useServiceLocation';
import { useToast } from '@/components/useToast';
import { ChangeAssignmentDialog } from '@/features/confirmations/ChangeAssignmentDialog';
import { EditReservationDetailsDialog } from '@/features/confirmations/EditReservationDetailsDialog';
import { notifyConfirmationQueueChanged } from '@/features/confirmations/confirmationQueueEvents';
import { usePendingConfirmationCount } from '@/features/confirmations/usePendingConfirmationCount';
import { OverrideConflictDialog } from '../OverrideConflictDialog';
import {
  INVALID_WINDOW_MESSAGE,
  isInvalidWindowError,
  toReservationActionErrorMessage,
} from '../reservationsError';
import { roleLimitedMessage } from '@/components/accessCopy';
import {
  CancelReservationDialog,
  CheckInDialog,
  NoShowDialog,
  RescheduleDialog,
} from '../ReservationActionDialogs';
import {
  bayAxis,
  deriveServiceColor,
  deriveStatusBucket,
  isPaymentInProgress,
  isUnpaid,
  localDateValueOf,
  operatingDayFor,
  outsideOperating,
  staffAxis,
  weekDates,
  type StaffRosterLane,
  type HourRange,
  type ResourceAxis,
  type ServiceColor,
  type StatusBucket,
} from './derive';
import { BookingsListDialog } from './BookingsListDialog';
import { PaymentAttentionList } from './PaymentAttentionList';
import { DayGrid } from './DayGrid';
import { SegmentedControl } from './SegmentedControl';
import { WeekGrid, type DatedLaneBlock } from './WeekGrid';
import { NowNextView } from './NowNextView';
import { UNRESOLVED_WINDOW_DAYS } from './nowNextBuckets';
import { ReservationDrawer } from './ReservationDrawer';
import { BlockedDrawer, type BlockedDrawerBlock } from './BlockedDrawer';
import { CreateReservationDialog, type CreateReservationPreset } from '../CreateReservationDialog';
import { BlockOffDialog } from '../BlockOffDialog';
import { TakePaymentDialog } from '../TakePaymentDialog';
import { UnpaidCheckInDialog } from '../UnpaidCheckInDialog';
import type { LaneBlock } from './DayGrid';
import { ReassignAssignmentDialog } from '../ReassignAssignmentDialog';
import {
  addDays,
  dayEndIso,
  dayStartIso,
  resolveCalendarZone,
  todayIn,
  zoneOrDevice,
} from '@/components/list/listDate';
import { useTenantZone } from '@/location/useCalendarZone';
import { ScreenError } from '@/components/ScreenError';

// --- View / layout model -------------------------------------------------------

/** The top-level view (DNKADM-0271): the task-first board vs the spatial grid. */
type TopView = 'nownext' | 'grid';
type CalendarView = 'day' | 'week';

/**
 * The calendar's **resource-axis mode** (DNKADM-0041) — which resource the grid groups
 * its rows/columns by. `bay` (default — today's behavior, 0032 + 0040) puts the tenant's
 * bays on the axis; `staff` (By Staff) puts the tenant's real staff (coaches) on it, so a
 * manager sees each coach's obligations. It is a **re-grouping** of the already-loaded
 * window — switching never refetches (like the layout toggles).
 */
type AxisMode = 'bay' | 'staff';

const AXIS_MODES: { value: AxisMode; label: string; icon: string }[] = [
  { value: 'bay', label: 'By Bay', icon: 'grid_view' },
  { value: 'staff', label: 'By Staff', icon: 'groups' },
];

// --- Date-window helpers (local time) ------------------------------------------

/** A `YYYY-MM-DD` date-input value for a Date in local time. */
function toDateInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Local midnight for a `YYYY-MM-DD` value. */
function dateAtStart(dateValue: string): Date {
  return new Date(`${dateValue}T00:00:00`);
}

/** The Monday of the week containing `dateValue` (weeks run Mon–Sun). */
function mondayOf(dateValue: string): Date {
  const d = dateAtStart(dateValue);
  const dow = d.getDay(); // 0=Sun … 6=Sat
  const deltaToMonday = (dow + 6) % 7; // Mon→0, Sun→6
  d.setDate(d.getDate() - deltaToMonday);
  return d;
}

/**
 * The one-row bar's date-control label, e.g. `Thu, Aug 13, 2026` (0300 A3), from the calendar
 * day's digits (DNKADM-0476 F-05: no device-zone `Date` render).
 */
function dateButtonLabel(dateValue: string): string {
  if (Number.isNaN(dateAtStart(dateValue).getTime())) return 'Pick a date';
  return dateLabel(dateValue) ?? 'Pick a date';
}

/**
 * The visible ISO window for the current date + view (day → that day; week → Mon–Sun), bounded
 * by the VENUE's day (DNKADM-0439: `timeZone` is the location zone; null is the device). Start
 * is the first day's first instant, end the last day's last millisecond: both inclusive, so
 * consecutive windows tile with no day dropped or counted twice. A bound is null when the
 * value is empty/unparseable (e.g. a cleared date input); the caller then omits it.
 */
function windowFor(
  view: CalendarView,
  dateValue: string,
  timeZone: string | null,
): { start: string | null; end: string | null } {
  const zone = zoneOrDevice(timeZone);
  if (view === 'day') {
    return { start: dayStartIso(dateValue, zone), end: dayEndIso(dateValue, zone) };
  }
  const monday = toDateInputValue(mondayOf(dateValue));
  return { start: dayStartIso(monday, zone), end: dayEndIso(addDays(monday, 6), zone) };
}

/** The human label for the current date + view (a single day, or the Mon–Sun range). */
function windowLabel(view: CalendarView, dateValue: string): string {
  if (view === 'day') {
    // From the calendar day's digits (DNKADM-0476 F-05), e.g. `Thursday, August 13, 2026`.
    return longDateLabel(dateValue) ?? dateValue;
  }
  // Built from the calendar dates by the shared formatter: `Sep 14 – 20, 2026`. (A day+year-only
  // `toLocaleDateString` rendered as "Sep 14 – 2026 (day: 20)" under ICU.)
  const monday = mondayOf(dateValue);
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  return dateRangeLabel(toDateInputValue(monday), toDateInputValue(sunday)) ?? dateValue;
}

/**
 * An operating day's open→close as an {@link HourRange} (whole hours), or null when
 * the day is closed / has no valid open<close pair. Drives the light "outside hours"
 * band over the part of the axis a booking extended beyond open/close.
 */
function operatingWindowOf(day: WeeklyHoursDay | null): HourRange | null {
  if (!day || !day.isAvailable || day.shifts.length === 0) return null;
  const parse = (t: string): number | null => {
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(t.trim());
    return m ? Number(m[1]) + Number(m[2]) / 60 : null;
  };
  // The day window spans the earliest shift start → latest shift end (DNKADM-0051 U2).
  let open: number | null = null;
  let close: number | null = null;
  for (const shift of day.shifts) {
    const s = parse(shift.startTime);
    const e = parse(shift.endTime);
    if (s !== null) open = open === null ? s : Math.min(open, s);
    if (e !== null) close = close === null ? e : Math.max(close, e);
  }
  if (open === null || close === null || close <= open) return null;
  return { startHour: Math.floor(open), endHour: Math.ceil(close) };
}

// --- Filters -------------------------------------------------------------------

const ALL = 'all';
type StatusFilter = 'all' | StatusBucket;

// --- View state in the URL (DNKADM-0430) -----------------------------------------

const TOP_VIEWS: readonly TopView[] = ['nownext', 'grid'];
const CALENDAR_VIEWS: readonly CalendarView[] = ['day', 'week'];
/** The Status filter's options (the `neutral` bucket is not one of them). */
const STATUS_FILTERS: readonly StatusFilter[] = [ALL, 'confirmed', 'pending', 'cancelled'];
const AXIS_VALUES: readonly AxisMode[] = ['bay', 'staff'];
/** An on/off param (`pay`, `cancelled`): only `1` is on; anything else, or absent, is off. */
const FLAG_ON = '1';
/** The all-dates payment-attention list's one page (DNKADM-0472): the oldest first. */
const PAYMENT_ATTENTION_PAGE_SIZE = 50;

/**
 * A real calendar day as `YYYY-MM-DD`: it must survive a round trip through a local
 * `Date`, so `2026-02-30`, `2026-13-01`, an empty string, and the partial years a date
 * input reports while a year is typed (`0002-…`, `0202-…`) are all rejected.
 */
function isCalendarDay(raw: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) && toDateInputValue(dateAtStart(raw)) === raw;
}

/** The `date` param: a real calendar day, or `fallback` (today) for anything else. */
function parseCalendarDate(raw: string, fallback: string): string {
  return isCalendarDay(raw) ? raw : fallback;
}

/** A free-value filter param (a service name, a staff id): empty reads as "all". */
function parseFilterValue(raw: string): string {
  return raw === '' ? ALL : raw;
}

/** A service present in the loaded window, with its stable derived color (legend). */
interface WindowService {
  name: string;
  color: ServiceColor;
}

/** A staff member present in the loaded window (the staff-filter options). */
interface WindowStaff {
  id: string;
  label: string;
}

/**
 * The active write action launched from the drawer. `reschedule`/`cancel` are the
 * DNKADM-0061 dialogs; `checkin`/`noshow` are the DNKADM-0146 attendance confirm-charge
 * dialogs (opened only for a card-on-file reservation — a non-card-on-file mark calls
 * the API directly with no dialog); `reassign` is the DNKADM-0211 staff/bay dialog;
 * `assignment` is the pending-request Change Resource/Staff dialog (`PUT …/details`),
 * reached from a confirm's coach conflict — "Change it" (DNKADM-0396) —
 * and, since DNKADM-0393 U2, from the drawer's own Change coach/bay on a PENDING
 * booking; `edit` is that same tab's Edit time dialog, likewise reachable from the
 * drawer on a pending booking.
 */
type ActiveAction =
  | 'reschedule'
  | 'cancel'
  | 'checkin'
  // DNKADM-0402 U5: checking in an UNPAID booking asks first — Take payment, or check
  // in without payment. It never blocks the check-in and never charges on its own.
  | 'checkin-unpaid'
  | 'takepayment'
  // DNKADM-0489: Take payment chosen inside Check in — a payment that lands also checks them in.
  | 'takepayment-checkin'
  | 'noshow'
  | 'reassign'
  | 'assignment'
  | 'edit'
  | null;

export function ReservationsCalendar() {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const themeMode = theme.palette.mode;
  const location = useServiceLocation();
  const serviceLocationId = location.selectedId;
  // The VENUE clock (DNKADM-0210 U2) — the selected location's IANA `timezone`, read off
  // the already-loaded location list (no extra fetch: `LocationProvider` loads the owned
  // v2 locations, which carry `timezone`). The grid positions blocks, the hour window and
  // the "now" line on it so a booking renders at the venue's wall-clock hour instead of
  // the operator's browser hour. FAIL-SOFT: null (location not resolved yet / no zone on
  // the record) → the layouts read each instant's own ISO offset, matching the drawer.
  // DNKADM-0212 U2 also uses this zone for date bucketing and window-clipped figures, and
  // DNKADM-0439 for "today" and the fetch window's day bounds (null there is the device).
  // DNKADM-0478: a location with no zone (or none selected) falls back to the tenant's zone.
  const tenantZone = useTenantZone();
  const venueTimeZone = useMemo<string | null>(
    () =>
      resolveCalendarZone(
        location.locations.find((l) => l.id === serviceLocationId)?.timezone,
        tenantZone,
      ),
    [location.locations, serviceLocationId, tenantZone],
  );
  const showToast = useToast();
  // DNKADM-0375: STAFF is front of house. dink-api opens every booking action on this
  // board to `STAFF_ROLES` (DNKAPI-0532) — book on behalf, confirm, decline, cancel,
  // reschedule, reassign, check in, no-show, take payment — so they render for every
  // operator. Money overrides inside those dialogs follow `useMoneyPermissions`.
  //
  // Block off stays OWNER/ADMIN: it writes bay and coach date exceptions, which are SET UP
  // (bay exceptions are ADMIN only; a STAFF session may write only its OWN coach record,
  // which it does from My schedule). A render hint only; the server decides.
  const canBlockOff = !useStaffTier();
  // Close out (DNKADM-0408 U2) is offered to an admin session only (`isAdmin`, OWNER/ADMIN/
  // SUPER_ADMIN) — the roles dink-api opens `…/payment/close-out` to. Never the floor. A
  // render hint only: the server's role check is the authority.
  const isAdmin = useOptionalOperator()?.isAdmin === true;
  // The tenant-wide pending-confirmation total (DNKADM-0276) — the SAME
  // event-synced source as the nav badge, so Now & Next's "Needs confirmation"
  // header can never contradict it. Lightweight (size-1 page; totalElements
  // only) — never a second window fetch.
  const pendingTotal = usePendingConfirmationCount();
  // Deep link (DNKADM-0147): `/reservations?reservation=<uuid>` opens that booking's
  // drawer. Read the param; the effect below opens the drawer (fetching the booking when
  // it isn't in the loaded window) and clears the param when the drawer closes.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkUuid = searchParams.get('reservation');

  // DNKADM-0430: the view state a link should reproduce lives in the URL, beside the
  // `reservation` deep link: the top view (`board`), Day/Week (`view`), the `date`, and
  // the filters (`status`, `service`, `staff`, and `pay` for "Payment needs attention"),
  // By Bay / By Staff (`axis`) and Show cancelled (`cancelled`). Each write REPLACES the
  // history entry. Every default is left out of the URL, so `/reservations` alone is
  // still today's Now & Next, and `?reservation=<uuid>` still lands on it unchanged.
  // "Today" is the VENUE's today (DNKADM-0439), fixed per zone so the defaults (and so
  // `setUrl`) stay stable; it moves when the locations load and again when the tenant zone
  // arrives (DNKADM-0478), at most twice.
  const today = useMemo(() => todayIn(venueTimeZone), [venueTimeZone]);
  const urlDefaults = useMemo(
    () => ({
      board: 'nownext',
      view: 'day',
      axis: 'bay',
      date: today,
      status: ALL,
      service: ALL,
      staff: ALL,
      pay: '',
      cancelled: '',
    }),
    [today],
  );
  const { values: urlValues, set: setUrl } = useListUrlState(urlDefaults);

  // The top-level view (DNKADM-0271): the task-first "Now & Next" board is the
  // DEFAULT landing; "Grid" holds the whole spatial machinery (Day/Week, layouts,
  // By Bay / By Staff, filters, legend) unchanged in capability. The last-used Grid
  // sub-state persists across toggles because `board` and `view` are separate params.
  const topView = parseOption(urlValues.board, TOP_VIEWS, 'nownext');
  const setTopView = (next: TopView) => setUrl({ board: next });
  const view = parseOption(urlValues.view, CALENDAR_VIEWS, 'day');
  const setView = (next: CalendarView) => setUrl({ view: next });
  // The resource-axis mode (DNKADM-0041) — By Bay (default) / By Staff, in the URL as
  // `axis`; switching it is a pure re-grouping of the already-loaded window (no refetch).
  const axisMode = parseOption(urlValues.axis, AXIS_VALUES, 'bay');
  const setAxisMode = (next: AxisMode) => setUrl({ axis: next });
  // The URL `date` is always a real day, so the window always has both bounds. The date
  // field types into a local draft: only a real day is written to the URL, so a cleared
  // or half-typed field (including a partial year) neither snaps back mid-typing nor
  // fetches; the board stays on the last real date. The draft follows the URL whenever
  // the date changes from elsewhere (Previous/Next/Today, a week-day click, a deep link).
  const date = parseCalendarDate(urlValues.date, today);
  const setDate = (next: string) => setUrl({ date: next });
  const [dateDraft, setDateDraft] = useState(date);
  useEffect(() => {
    setDateDraft(date);
  }, [date]);
  const onDateFieldChange = (next: string) => {
    setDateDraft(next);
    if (isCalendarDay(next)) setDate(next);
  };

  const filterStatus = parseOption(urlValues.status, STATUS_FILTERS, ALL);
  const setFilterStatus = (next: StatusFilter) => setUrl({ status: next });
  const filterService = parseFilterValue(urlValues.service);
  const setFilterService = (next: string) => setUrl({ service: next });
  const filterStaff = parseFilterValue(urlValues.staff);
  const setFilterStaff = (next: string) => setUrl({ staff: next });
  // "Payment needs attention" (DNKADM-0408 U1) — unlike the three above it is SERVER-side:
  // dink-api's `paymentAttention=true` (DNKAPI-0563) narrows the window to stranded and
  // needs-review bookings.
  const filterPaymentAttention = urlValues.pay === FLAG_ON;
  const setFilterPaymentAttention = (on: boolean) => setUrl({ pay: on ? FLAG_ON : null });
  // One-row control bar (DNKADM-0300 A): the Filters(N) collapse owns a popover
  // anchor. `Show cancelled` deliberately stays OUTSIDE the Filters collapse — it
  // is a visibility toggle, not a filter, and hiding it would let an operator
  // stare at a board with cancelled bookings suppressed and no visible reason why.
  //
  // The date control is NOT one of them any more (DNKADM-0393 U1): it is a plain
  // field, so the native calendar anchors under the field itself.
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  // The calendar icon's one click opens the native picker (DNKADM-0393 U1): the
  // button's own click is the user activation `showPicker` needs.
  const openDatePicker = useCallback(() => {
    const input = dateInputRef.current;
    if (!input) return;
    input.focus();
    try {
      (input as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
    } catch {
      // Not user-activated or unsupported — the focused input is the fallback.
    }
  }, []);
  const [filtersAnchor, setFiltersAnchor] = useState<HTMLElement | null>(null);
  // The after-hours list (DNKADM-0300 E) — the day's bookings outside the
  // operating window, which the load metric deliberately doesn't cover.
  const [afterHoursOpen, setAfterHoursOpen] = useState(false);
  // Grid cleanup (DNKADM-0271): cancelled reservations collapse OUT of the grid by
  // default; this toggle brings them back. An explicit "Cancelled" status filter
  // always shows them (else that filter would render an empty grid).
  const showCancelled = urlValues.cancelled === FLAG_ON;
  const setShowCancelled = (on: boolean) => setUrl({ cancelled: on ? FLAG_ON : null });

  // The selected booking (the detail-drawer target) — set by the day/week grids on
  // click. The drawer reads it; the reschedule wizard + cancel dialog (the write
  // actions) launch from the drawer via `activeAction`.
  const [selectedBooking, setSelectedBooking] = useState<Reservation | null>(null);
  const [activeAction, setActiveAction] = useState<ActiveAction>(null);
  // Confirm re-checks the booking at confirm time: the coach (DNKAPI-0542) and, since
  // DNKAPI-0636 D-06, every create-time gate (DNKAPI-0549 `overrideBlocks`). Any of them opens
  // the shared refusal dialog (DNKADM-0396) instead of the error toast, read through
  // `parseOverrideRefusal`. `sent` is the body that was refused, so an acknowledged resend
  // that hits the next guard keeps the flag already ticked (see `confirmBooking`).
  const [confirmConflict, setConfirmConflict] = useState<{
    reservation: Reservation;
    view: ConflictRefusal;
    sent: ConfirmReservationInput;
  } | null>(null);
  // The create-on-behalf dialog (DNKADM-0061 U2).
  // Book on behalf: `{}` opens the dialog plain; `{ preset }` opens it prefilled from a tap on
  // an empty grid cell (DNKADM-0389 U2). `null` = closed.
  const [creating, setCreating] = useState<{ preset?: CreateReservationPreset } | null>(null);
  // The Blocked band the operator tapped (DNKADM-0389 U3); null = the drawer is closed.
  const [selectedBlock, setSelectedBlock] = useState<BlockedDrawerBlock | null>(null);
  // Block off (DNKADM-0373 U1) — the dialog that writes per-bay / per-coach date
  // exceptions for a window of the board's date.
  const [blockingOff, setBlockingOff] = useState(false);

  // Now & Next is a DAY board — while it's active the fetch window is the single
  // day regardless of the Grid's last-used Day/Week sub-state.
  const effectiveView: CalendarView = topView === 'nownext' ? 'day' : view;
  const { start, end } = useMemo(
    () => windowFor(effectiveView, date, venueTimeZone),
    [effectiveView, date, venueTimeZone],
  );

  // Location-scoped, windowed fetch (DNKADM-0061 — re-pointed to the owned v2
  // `/v2/admin/reservations`; the active location id from DNKADM-0051 is the REQUIRED
  // `locationId`). The fetcher returns null when there is no selected location yet, so
  // a missing/garbage id never hits the wire (fail-closed). `serviceLocationId` is part
  // of the dep key → a location change refetches.
  //
  // The payment-attention filter rides on this same read, but only while the Grid is
  // showing: the Filters menu lives on the Grid, and Now & Next reads this window too —
  // it must never be narrowed by a filter it does not show.
  const paymentAttention = topView === 'grid' && filterPaymentAttention;
  const resource = useResource(
    useCallback(async () => {
      if (!serviceLocationId) return null;
      return listReservationWindow({
        locationId: serviceLocationId,
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        ...(paymentAttention ? { paymentAttention: true } : {}),
      });
    }, [serviceLocationId, start, end, paymentAttention]),
    [serviceLocationId, start, end, paymentAttention],
  );

  // DNKADM-0472: with the filter on, the Grid also lists every booking at the location
  // whose payment needs attention across ALL dates (no window), oldest first — a stuck
  // payment on another day is no longer found only by paging the Grid.
  const paymentAttentionAll = useResource(
    useCallback(async () => {
      if (!serviceLocationId || !paymentAttention) return null;
      return listPaymentAttentionReservations({
        locationId: serviceLocationId,
        size: PAYMENT_ATTENTION_PAGE_SIZE,
      });
    }, [serviceLocationId, paymentAttention]),
    [serviceLocationId, paymentAttention],
  );

  // Stable empty-array identity when nothing is loaded, so the derived memos below
  // don't re-run every render.
  const all = useMemo(() => resource.data?.reservations ?? [], [resource.data]);

  // The selected location's operating hours (DNKADM-0039) — scoped to the LOCATION
  // only (NOT the date), so switching the day within a location doesn't refetch.
  // FAIL-SOFT: any failure (or no selected location) → null `days`, and the axis
  // falls back to the fixed 9–21 default window (still extended by bookings); the
  // calendar is NEVER blocked or emptied over a missing operating-hours read.
  const operatingHours = useResource(
    useCallback(async () => {
      if (!serviceLocationId) return null;
      return getOperatingHours(serviceLocationId);
    }, [serviceLocationId]),
    [serviceLocationId],
  );
  const operatingDays = useMemo<WeeklyHoursDay[] | null>(
    () => (operatingHours.status === 'ready' ? (operatingHours.data?.days ?? null) : null),
    [operatingHours.status, operatingHours.data],
  );

  // The bay ROSTER — the tenant's active bays from `GET /v2/admin/resources`, which
  // defines the grid's bay axis independently of the bookings (so an empty day still
  // renders one row per bay instead of a blank grid). FAIL-SOFT: a load failure (or a
  // successful empty roster) resolves to `[]`, which the calendar treats as "no roster"
  // and degrades to bay-from-bookings
  // (today's behavior). So the roster can never blank or error the calendar.
  const resources = useResource(
    useCallback(async () => {
      if (!serviceLocationId) return [];
      try {
        const rows = await listResources({ status: 'active' });
        return rows.map((r) => ({ id: r.id, label: r.name.trim() || r.id }));
      } catch {
        return [];
      }
    }, [serviceLocationId]),
    [serviceLocationId],
  );
  // An empty roster carries no axis → treat as absent (fall back to bay-from-bookings).
  const roster = useMemo(
    () => (resources.data && resources.data.length > 0 ? resources.data : undefined),
    [resources.data],
  );

  // The real-staff ROSTER — the tenant's active staff from `GET /v2/admin/staff`,
  // the By-Staff axis source, so an empty day still renders one row per coach.
  // FAIL-SOFT: a load failure (or a successful empty roster) resolves to `[]`, which
  // the By-Staff axis treats as "no roster" and degrades to coaches-from-bookings.
  // So the roster can never blank or error the calendar.
  // Fetched unconditionally (not gated on `axisMode`) so toggling to By Staff is a pure
  // re-grouping with no refetch; in By Bay it is simply unused.
  const staff = useResource(
    useCallback(async () => {
      if (!serviceLocationId) return [];
      try {
        const rows = await listStaff({ status: 'active' });
        return rows.map((s) => {
          const name = `${s.firstName} ${s.lastName}`.trim();
          const lane: StaffRosterLane = { id: s.id, label: name || s.email || s.id };
          const firstName = s.firstName.trim();
          if (firstName !== '') lane.firstName = firstName;
          return lane;
        });
      } catch {
        return [];
      }
    }, [serviceLocationId]),
    [serviceLocationId],
  );
  // An empty roster carries no axis → treat as absent (degrade to coaches-from-bookings).
  const staffRoster = useMemo(
    () => (staff.data && staff.data.length > 0 ? staff.data : undefined),
    [staff.data],
  );

  // Block-offs on the board (DNKADM-0373 U2): each roster lane's date exceptions,
  // read once per roster (the list carries every date; the visible date is filtered
  // below) and re-read after the Block off dialog writes. FAIL-SOFT, like the rosters:
  // a failed read is an empty list — a block that cannot be shown never blanks the
  // board. Both rosters' exceptions are read whenever the roster exists (DNKADM-0388
  // U2: By Bay names the coaches blocked that day, so a coach closure cannot hide
  // behind the axis toggle).
  const bayExceptions = useResource(
    useCallback(async () => {
      if (!roster) return [];
      const perBay = await Promise.all(
        roster.map(async (bay) => {
          try {
            const rows = await listResourceDateExceptions(bay.id);
            return rows.map((e) => ({ laneKey: bay.id, ...e }));
          } catch {
            return [];
          }
        }),
      );
      return perBay.flat();
    }, [roster]),
    [roster],
  );
  const staffExceptionsWanted = staffRoster !== undefined;
  const staffExceptions = useResource(
    useCallback(async () => {
      if (!staffExceptionsWanted || !staffRoster) return [];
      const perCoach = await Promise.all(
        staffRoster.map(async (coach) => {
          try {
            const rows = await listStaffDateExceptions(coach.id);
            return rows.map((e) => ({ laneKey: coach.id, ...e }));
          } catch {
            return [];
          }
        }),
      );
      return perCoach.flat();
    }, [staffExceptionsWanted, staffRoster]),
    [staffExceptionsWanted, staffRoster],
  );
  const refetchBayExceptions = bayExceptions.refetch;
  const refetchStaffExceptions = staffExceptions.refetch;
  const refetchBlocks = useCallback(() => {
    refetchBayExceptions();
    refetchStaffExceptions();
  }, [refetchBayExceptions, refetchStaffExceptions]);

  // The unresolved-queue candidates (DNKADM-0350 U1) — CONFIRMED reservations
  // from the last UNRESOLVED_WINDOW_DAYS days, fetched INDEPENDENTLY of the
  // selected `date`/day window: a forgotten booking must stay visible no
  // matter what day the operator is currently browsing (the whole failure
  // Teresa's booking exposed — it fell out of view once the day passed).
  // `status: 'confirmed'` is forwarded as a server-side courtesy; the pure
  // `unresolvedReservations` re-checks status/window client-side (defensive,
  // mirrors every other read in this dir), so a looser server filter can't
  // leak a checked-in/cancelled row into the queue. Fetched unconditionally
  // (mirrors the operating-hours/roster fetches) — cheap, and Now & Next may
  // become the active view again without a stale queue.
  const unresolvedCandidates = useResource(
    useCallback(async () => {
      if (!serviceLocationId) return [];
      const nowMs = Date.now();
      const windowStart = new Date(nowMs - UNRESOLVED_WINDOW_DAYS * 24 * 60 * 60 * 1000);
      return listReservations({
        locationId: serviceLocationId,
        start: windowStart.toISOString(),
        end: new Date(nowMs).toISOString(),
        status: 'confirmed',
      });
    }, [serviceLocationId]),
    [serviceLocationId],
  );
  const unresolvedRefetch = unresolvedCandidates.refetch;

  // Services + staff present in the loaded window (legend + filter options).
  const windowServices = useMemo<WindowService[]>(() => {
    const seen = new Map<string, WindowService>();
    for (const r of all) {
      const name = r.serviceName;
      if (name && !seen.has(name)) seen.set(name, { name, color: deriveServiceColor(name, themeMode) });
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [all, themeMode]);

  const windowStaff = useMemo<WindowStaff[]>(() => {
    // Filter options come from the v2 canonical staff fields, NOT the `staff`
    // alias — the alias deliberately mirrors the RESOURCE on staffless
    // bookings (the bay-axis grouping contract), which listed BAYS in the
    // Staff filter and kept it visible on staff-less windows (DNKADM-0304 U2
    // alias sweep, the 0303-U7 class). No real staff in the window → no
    // options → the filter hides (the existing degrade-when-absent rule).
    const seen = new Map<string, WindowStaff>();
    for (const r of all) {
      if (r.assignedStaffId !== null) {
        const label = r.assignedStaffName ?? r.assignedStaffId;
        if (!seen.has(r.assignedStaffId)) {
          seen.set(r.assignedStaffId, { id: r.assignedStaffId, label });
        }
      }
    }
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [all]);

  // The staff filter is hidden when NO booking carries staff (degrade-when-absent).
  const hasStaff = windowStaff.length > 0;
  // A staff or service filter (from a link, or kept across a date change) that no booking
  // in this window carries stays selected and visible as its own option, so the select
  // says why the board is empty and "All ..." clears it. It is never reset on its own:
  // "that coach has nothing today" is a real answer (DNKADM-0430).
  const staffFilterOutsideWindow =
    filterStaff !== ALL && !windowStaff.some((s) => s.id === filterStaff);
  const serviceFilterOutsideWindow =
    filterService !== ALL && !windowServices.some((s) => s.name === filterService);
  // The Filters(N) badge — active (non-"all") filters, so the collapsed control
  // always states how many filters are narrowing the board (DNKADM-0300 A1).
  const activeFilterCount =
    [filterStatus, filterService, filterStaff].filter((v) => v !== ALL).length +
    (filterPaymentAttention ? 1 : 0);

  // Client-side filtering over the loaded window (mirrors the design's `pass(b)`).
  const filtered = useMemo(
    () =>
      all.filter((r) => {
        // The server already narrowed the read (DNKAPI-0563); re-checked here like every
        // other read in this dir, so a server that ignores the param lists nothing
        // rather than everything.
        if (paymentAttention && r.paymentStranded !== true && r.paymentNeedsReview !== true) {
          return false;
        }
        if (filterStatus !== ALL && deriveStatusBucket(r.status) !== filterStatus) return false;
        if (filterService !== ALL && r.serviceName !== filterService) return false;
        // Matches the canonical field the options above are built from — a
        // staffless booking matches no staff filter (DNKADM-0304 U2).
        if (filterStaff !== ALL && (r.assignedStaffId ?? '') !== filterStaff) return false;
        return true;
      }),
    [all, paymentAttention, filterStatus, filterService, filterStaff],
  );

  // Grid cleanup (DNKADM-0271): cancelled bookings collapse out of the grid unless
  // the operator opts back in — or has explicitly filtered TO cancelled. The Now &
  // Next board never shows cancelled (its bucketing drops them), so this feeds the
  // Grid surfaces only.
  //
  // "Payment needs attention" (DNKADM-0408 U1) lifts the collapse too: a cancelled
  // booking whose money moved is one of the core needs-review cases, and a filter that
  // hid it behind a second toggle would list exactly the bookings least in need of a look.
  const gridReservations = useMemo(
    () =>
      showCancelled || filterStatus === 'cancelled' || paymentAttention
        ? filtered
        : filtered.filter((r) => deriveStatusBucket(r.status) !== 'cancelled'),
    [filtered, showCancelled, filterStatus, paymentAttention],
  );

  // The active resource axis (DNKADM-0041) — the seam the day layouts + week Heatmap/Load
  // bars consume. **By Bay** (default) is the roster-driven bay axis (DNKADM-0040 — the
  // roster bays always shown ∪ an "Unassigned" lane for any unmatched booking; the
  // bookings-derived set as the fail-soft fallback) — byte-identical to today. **By Staff**
  // is the staff axis (the coach roster ∪ "Unassigned"; coaches-from-bookings when the
  // roster is absent). Pure re-grouping of the SAME `filtered` window — switching the mode
  // never refetches. `axisLabel` drives the gutter/column header word.
  const axis = useMemo<ResourceAxis>(
    () =>
      axisMode === 'staff'
        ? staffAxis(gridReservations, staffRoster)
        : bayAxis(gridReservations, roster),
    [axisMode, gridReservations, roster, staffRoster],
  );
  const axisLabel = axisMode === 'staff' ? 'STAFF' : 'BAY';
  // Whether the active axis is roster-backed (every lane shown even with zero bookings) —
  // drives the grid's "render the axis on an empty window" decision (the 0040 behavior,
  // now also for the staff roster).
  const hasAxisRoster = axisMode === 'staff' ? staffRoster !== undefined : roster !== undefined;

  // The visible date's block-offs on the active axis (DNKADM-0373 U2): an exception
  // applies when it is for this date and either every location or THIS location.
  const dayBlocks = useMemo<LaneBlock[]>(() => {
    const rows = axisMode === 'staff' ? staffExceptions.data : bayExceptions.data;
    return (rows ?? [])
      .filter(
        (e) =>
          e.exceptionDate === date &&
          (e.scope === 'ALL' || e.locationId === serviceLocationId),
      )
      .map((e) => ({ laneKey: e.laneKey, isAvailable: e.isAvailable, shifts: e.shifts, id: e.id }));
  }, [axisMode, staffExceptions.data, bayExceptions.data, date, serviceLocationId]);

  // The coaches closed or partly closed on the visible date (DNKADM-0388 U2), by first
  // name, for the By Bay line. An open exception with NO shifts is "use the location
  // window" and blocks nothing, so it does not count.
  const blockedCoaches = useMemo<string[]>(() => {
    if (!staffRoster) return [];
    const closed = new Set(
      (staffExceptions.data ?? [])
        .filter(
          (e) =>
            e.exceptionDate === date &&
            (e.scope === 'ALL' || e.locationId === serviceLocationId) &&
            (!e.isAvailable || e.shifts.length > 0),
        )
        .map((e) => e.laneKey),
    );
    return staffRoster
      .filter((lane) => closed.has(lane.id))
      .map((lane) => lane.firstName ?? lane.label)
      .sort((a, b) => a.localeCompare(b));
  }, [staffRoster, staffExceptions.data, date, serviceLocationId]);
  // The mirror (DNKADM-0389 U1): the bays closed or partly closed on the visible date, by
  // name, for the By Staff line — the same read and the same rule as the coaches line.
  const blockedBays = useMemo<string[]>(() => {
    if (!roster) return [];
    const closed = new Set(
      (bayExceptions.data ?? [])
        .filter(
          (e) =>
            e.exceptionDate === date &&
            (e.scope === 'ALL' || e.locationId === serviceLocationId) &&
            (!e.isAvailable || e.shifts.length > 0),
        )
        .map((e) => e.laneKey),
    );
    return roster
      .filter((bay) => closed.has(bay.id))
      .map((bay) => bay.label.trim() || bay.id)
      .sort((a, b) => a.localeCompare(b));
  }, [roster, bayExceptions.data, date, serviceLocationId]);
  const isToday = date === todayIn(venueTimeZone);
  // One line, two directions: on By Bay it names the blocked coaches and offers By Staff; on
  // By Staff it names the blocked bays and offers By Bay. Shown only when something is blocked.
  const blockedLine =
    axisMode === 'bay'
      ? { names: blockedCoaches, what: 'Coaches', action: 'View By Staff', to: 'staff' as AxisMode, testId: 'coaches-blocked-line' }
      : { names: blockedBays, what: 'Bays', action: 'View By Bay', to: 'bay' as AxisMode, testId: 'bays-blocked-line' };

  // A tap on open space in a lane (DNKADM-0389 U2): open Book on behalf on this date, at the
  // tapped half hour, with the row's bay or coach as the starting point. The "Unassigned" lane
  // names nobody, so it opens nothing.
  const onSlotClick = useCallback(
    (laneKey: string, startHour: number) => {
      if (laneKey === axis.unassignedKey) return;
      const h = Math.floor(startHour);
      const m = Math.round((startHour - h) * 60);
      const startTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const preset: CreateReservationPreset =
        axisMode === 'staff'
          ? { date, startTime, staffId: laneKey }
          : { date, startTime, resourceId: laneKey };
      setCreating({ preset });
    },
    [axis.unassignedKey, axisMode, date],
  );

  // The visible week's block-offs on the active axis (HOTFIX: the Week view now draws them
  // too) — the same read and location rule as `dayBlocks`, for each of the seven dates.
  const weekBlocks = useMemo<DatedLaneBlock[]>(() => {
    if (view !== 'week') return [];
    const dates = new Set(weekDates(date).map(toDateInputValue));
    const rows = axisMode === 'staff' ? staffExceptions.data : bayExceptions.data;
    return (rows ?? [])
      .filter(
        (e) =>
          dates.has(e.exceptionDate) &&
          (e.scope === 'ALL' || e.locationId === serviceLocationId),
      )
      .map((e) => ({ date: e.exceptionDate, laneKey: e.laneKey, isAvailable: e.isAvailable, shifts: e.shifts, id: e.id }));
  }, [view, axisMode, staffExceptions.data, bayExceptions.data, date, serviceLocationId]);

  // The day's operating window — the Day grid computes its own envelope axis
  // from this (DNKADM-0300 geometry); it also feeds the load signal, the
  // after-hours partition, and Now & Next's walk-in panel.
  const dayOperating = useMemo<HourRange | null>(
    () => operatingWindowOf(operatingDayFor(operatingDays, date)),
    [operatingDays, date],
  );

  // A tap on a Blocked band (DNKADM-0389 U3): everything the drawer shows is what the board holds.
  const onBlockSelect = useCallback(
    (block: LaneBlock, blockDate: string = date) => {
      const lane = axis.lanes.find((l) => l.key === block.laneKey);
      setSelectedBlock({
        kind: axisMode === 'staff' ? 'coach' : 'bay',
        targetId: block.laneKey,
        targetName: lane?.label ?? block.laneKey,
        date: blockDate,
        exceptionId: block.id ?? null,
        isAvailable: block.isAvailable,
        shifts: block.shifts,
        // The Week view taps a band on any day: that day's window, not the selected day's.
        operating:
          blockDate === date
            ? dayOperating
            : operatingWindowOf(operatingDayFor(operatingDays, blockDate)),
      });
    },
    [axis.lanes, axisMode, date, dayOperating, operatingDays],
  );

  // The day's bookings entirely outside the operating window (DNKADM-0300 E).
  // The control renders only at a non-zero count (omitted at zero, per the
  // spec's "omit or disable" — omitted, because a dead control with no
  // possible content is noise on an already-full bar).
  const afterHours = useMemo(
    () =>
      outsideOperating(
        gridReservations.filter(
          (r) => localDateValueOf(r.startTime, venueTimeZone) === date,
        ),
        dayOperating,
        venueTimeZone,
      ),
    [gridReservations, date, dayOperating, venueTimeZone],
  );

  function goPrev() {
    setDate(addDays(date, effectiveView === 'day' ? -1 : -7));
  }
  function goNext() {
    setDate(addDays(date, effectiveView === 'day' ? 1 : 7));
  }
  // What one Previous/Next step moves, for the step buttons' tooltip + accessible name.
  const stepUnit = effectiveView === 'day' ? 'day' : 'week';
  function goToday() {
    setDate(todayIn(venueTimeZone));
  }

  // Heatmap-cell / load-bar day click → switch to Day view on that date (the week
  // layouts surface a specific `YYYY-MM-DD`; the shell owns view + date).
  const openDay = useCallback(
    (target: string) => setUrl({ date: target, view: 'day' }),
    [setUrl],
  );

  // DNKADM-0430: the router's setters change identity on every URL change, and the view
  // state now lives in the URL. The deep-link effect below reads them through these refs,
  // so a URL-only change (a filter, the axis, closing the drawer) does not re-run that
  // effect. A board, view or date change still starts a new window read, which re-runs it
  // exactly as before 0430.
  const setSearchParamsRef = useRef(setSearchParams);
  const setUrlRef = useRef(setUrl);
  useEffect(() => {
    setSearchParamsRef.current = setSearchParams;
    setUrlRef.current = setUrl;
  });

  // --- Drawer + write actions (U4) ---------------------------------------------
  // Clear the `?reservation=` deep-link param (DNKADM-0147) so the drawer doesn't
  // reopen on a remount — mirrors the payments screen's connect-param clear. The view
  // params (DNKADM-0430) are kept.
  const clearDeepLinkParam = useCallback(() => {
    setSearchParamsRef.current(
      (prev) => {
        if (!prev.has('reservation')) return prev;
        const next = new URLSearchParams(prev);
        next.delete('reservation');
        return next;
      },
      { replace: true },
    );
  }, []);
  const closeDrawer = useCallback(() => {
    setSelectedBooking(null);
    clearDeepLinkParam();
  }, [clearDeepLinkParam]);

  // A write succeeded: clear the action + selection, toast, and refetch the window — and
  // the all-dates payment-attention list (DNKADM-0472), which a write can resolve.
  const { refetch: windowRefetch } = resource;
  const { refetch: attentionRefetch } = paymentAttentionAll;
  const refetch = useCallback(() => {
    windowRefetch();
    attentionRefetch();
  }, [windowRefetch, attentionRefetch]);
  const refreshPendingCharge = useCallback(
    async (uuid: string) => {
      const refreshed = await getReservation(uuid);
      setSelectedBooking(refreshed);
      refetch();
    },
    [refetch],
  );
  const onActionDone = useCallback(
    (message: string) => {
      setActiveAction(null);
      setSelectedBooking(null);
      setCreating(null);
      clearDeepLinkParam();
      showToast(message, 'success');
      refetch();
      // A check-in/no-show/cancel can resolve (or newly create) an unresolved
      // row (DNKADM-0350 U1) — refresh the queue alongside the day window.
      unresolvedRefetch();
    },
    [showToast, refetch, clearDeepLinkParam, unresolvedRefetch],
  );

  // A reassignment succeeded (DNKADM-0211 U2). The `200` IS the updated reservation, so
  // it REPLACES the drawer's copy in place — the same replace-then-refetch path the
  // pending-charge writes use — instead of clearing the selection: the admin keeps the
  // panel open on the booking they just changed, with the new Staff/Bay showing, while
  // the window refetches behind it. Only the assignment changed (time/price/credits/
  // status are untouched by the endpoint).
  const onReassigned = useCallback(
    (updated: Reservation) => {
      setActiveAction(null);
      setSelectedBooking(updated);
      showToast('Reservation reassigned.', 'success');
      refetch();
    },
    [showToast, refetch],
  );

  // DNKADM-0257 extends the same replace-then-refetch to confirm: the mutation's 200 IS
  // the updated reservation, so it replaces the drawer's copy in place (drawer stays
  // open, new status showing) while the window refetch reconciles behind it — the
  // operator sees the mutation reflect without a network round trip. Cancel no longer
  // takes this path (DNKADM-0415): its response is not a reservation.
  const onMutationReflected = useCallback(
    (updated: Reservation, message: string) => {
      setActiveAction(null);
      setSelectedBooking(updated);
      showToast(message, 'success');
      refetch();
      // Same reasoning as `onActionDone` — a write reflected here (e.g. a confirm) can
      // resolve an unresolved row (DNKADM-0350 U1).
      unresolvedRefetch();
    },
    [showToast, refetch, unresolvedRefetch],
  );

  // Check payment answered (DNKADM-0408 U1): the same replace-then-refetch Take payment's
  // success uses, minus the toast — the drawer says the outcome inline, where the desk
  // acts on it. The replace is guarded by uuid so a late answer for a booking the drawer
  // has since moved off never overwrites the one now open.
  const onPaymentChecked = useCallback(
    (updated: Reservation) => {
      setSelectedBooking((prev) =>
        prev !== null && prev.reservationUuid === updated.reservationUuid ? updated : prev,
      );
      refetch();
      unresolvedRefetch();
    },
    [refetch, unresolvedRefetch],
  );

  // A no-dialog write (the non-card-on-file attendance path): await the action, then run
  // the shared success (toast + refetch + close) or surface the API error as a toast.
  const runDirectMark = useCallback(
    async (action: Promise<unknown>, message: string) => {
      try {
        await action;
        onActionDone(message);
      } catch (err) {
        showToast(toReservationActionErrorMessage(err), 'error');
      }
    },
    [onActionDone, showToast],
  );

  // Check-in attendance (DNKADM-0146) keeps its payment-mode routing: card-on-file (or
  // an absent paymentMode) opens the amount dialog; settled modes mark directly.
  const needsCheckInChargeStep = useCallback(
    (r: Reservation) => r.paymentMode === 'card_on_file' || r.paymentMode === null,
    [],
  );
  const markCheckIn = useCallback(
    (r: Reservation) => {
      // DNKADM-0402 U5: a booking that still owes money asks first — unpaid, or a
      // payment pay-api never confirmed (Operator decision, 2026-09-22), which the
      // prompt states without offering to charge it again. Check-in is never blocked by
      // either: the prompt's second action checks them in and changes no money state.
      if (isUnpaid(r) || isPaymentInProgress(r)) {
        setActiveAction('checkin-unpaid');
        return;
      }
      if (needsCheckInChargeStep(r)) {
        setActiveAction('checkin');
        return;
      }
      if (r.reservationUuid) void runDirectMark(checkInReservation(r.reservationUuid, {}), 'Reservation checked in.');
    },
    [needsCheckInChargeStep, runDirectMark],
  );
  // DNKADM-0188: every no-show state needs the dialog. It renders truthful credit /
  // configured-none / card-fee / pre-0317 copy and owns the corresponding charge body.
  const markNoShowAction = useCallback(() => setActiveAction('noshow'), []);

  // Confirm an unconfirmed (`new`) booking (DNKADM-0147) — a plain status change (no
  // dialog). DNKADM-0257: the 200 is the confirmed reservation, so it replaces the
  // drawer's copy in place (replace-then-refetch) instead of closing the drawer.
  // `input` carries acknowledge flags ONLY on a "Book anyway" resend from the refusal
  // dialog; every plain Confirm (drawer, Now & Next) sends none.
  const confirmBooking = useCallback(
    (r: Reservation, input: ConfirmReservationInput = {}) => {
      const uuid = r.reservationUuid;
      if (!uuid) return;
      void (async () => {
        try {
          const updated = await confirmReservation(uuid, input);
          onMutationReflected(updated, 'Reservation confirmed.');
          // The booking left the tenant-wide pending queue — refresh the shared
          // count (nav badge + this board's header) via the queue event (DNKADM-0276).
          notifyConfirmationQueueChanged();
        } catch (err) {
          // A coach double-book (DNKADM-0386 U1) is a dialog, never a toast. The
          // selection is NOT touched here (a Confirm from the Now & Next board has no
          // drawer open, and Cancel must leave it that way) — only "Choose another
          // coach" selects the booking, because the picker renders the selection.
          const refusal = parseOverrideRefusal(err);
          if (refusal) {
            setConfirmConflict({ reservation: r, view: refusal, sent: input });
            return;
          }
          if (v2ErrorCode(err) === 'RESERVATION_CHANGED') {
            // DNKAPI-0636 D-04: someone else changed it first and nothing was written. The
            // board's copy is stale, so reload it before the desk tries again.
            showToast(
              'This booking was changed by someone else. The calendar has been refreshed; check it and confirm again.',
              'error',
            );
            refetch();
            return;
          }
          showToast(toReservationActionErrorMessage(err), 'error');
        }
      })();
    },
    [onMutationReflected, showToast, refetch],
  );

  // --- Now & Next inline actions (DNKADM-0271) ----------------------------------
  // Each routes through the EXISTING shell state machine — the same dialogs, the
  // same success paths (Confirm replaces the drawer's copy, 0257; Check-in, No-show
  // and Decline close it, Decline via the cancel path, DNKADM-0415) — so the board
  // adds no second action implementation. Check-in keeps its payment-mode dialog
  // routing; No-show and Decline (cancel) open their dialogs against the selected booking.
  const boardCheckIn = useCallback(
    (r: Reservation) => {
      setSelectedBooking(r);
      markCheckIn(r);
    },
    [markCheckIn],
  );
  const boardNoShow = useCallback((r: Reservation) => {
    setSelectedBooking(r);
    setActiveAction('noshow');
  }, []);
  // DNKADM-0447: the board's Decline on a NEW payment hold opens the decline dialog (no
  // Refund choice, sends `full`); the drawer's Cancel booking on the same row stays a cancel.
  const [declineIntent, setDeclineIntent] = useState(false);
  const boardDecline = useCallback((r: Reservation) => {
    setSelectedBooking(r);
    setDeclineIntent(true);
    setActiveAction('cancel');
  }, []);

  // Deep link (DNKADM-0147): open the drawer for `?reservation=<uuid>`. Handled ONCE per
  // param value (the ref guards re-runs); if the booking is in the loaded window select
  // it, otherwise fetch it (and move the calendar to its date) and open the drawer. A
  // not-found uuid toasts and clears the param. Waits for the window to be `ready` so the
  // in-view check is decided against the loaded data before falling back to a fetch.
  const handledDeepLinkRef = useRef<string | null>(null);
  // The location-scoped window (null until the resolved-location fetch returns — the
  // fetcher yields null before a location resolves, a premature `ready` we must NOT treat
  // as an authoritative in-view answer).
  const loadedReservations = resource.data?.reservations ?? null;
  useEffect(() => {
    if (deepLinkUuid === null) {
      // Param cleared → reset the guard so a later same-uuid link re-triggers.
      handledDeepLinkRef.current = null;
      return;
    }
    if (handledDeepLinkRef.current === deepLinkUuid || loadedReservations === null) return;
    handledDeepLinkRef.current = deepLinkUuid;

    // A deep link that arrives while a write surface is open (DNKADM-0386 U1: the coach
    // conflict dialog's "View on calendar", raised over the reassign / assignment / book-
    // on-behalf dialogs) dismisses that surface first. The reassign and assignment
    // dialogs render `reservation={selectedBooking}`, so selecting the linked booking
    // underneath an open one would silently re-target it; the create dialog would keep
    // the drawer behind it. The linked booking's drawer is what the operator asked for.
    setActiveAction(null);
    setCreating(null);
    setConfirmConflict(null);

    const inView = loadedReservations.find((r) => r.reservationUuid === deepLinkUuid);
    if (inView) {
      setSelectedBooking(inView);
      return;
    }
    let alive = true;
    void getReservation(deepLinkUuid)
      .then((r) => {
        if (!alive) return;
        setSelectedBooking(r);
        // Move the calendar to the booking's date so it's visible behind the drawer.
        const dv = localDateValueOf(r.startTime, venueTimeZone);
        if (dv) setUrlRef.current({ date: dv });
      })
      .catch(() => {
        if (!alive) return;
        showToast('That reservation could not be found.', 'error');
        clearDeepLinkParam();
      });
    return () => {
      alive = false;
    };
  }, [deepLinkUuid, loadedReservations, venueTimeZone, showToast, clearDeepLinkParam]);

  // --- Location gating: resolve a location BEFORE the first fetch ---------------
  if (location.status === 'loading') {
    return <CalendarLoading />;
  }
  if (location.status === 'error') {
    return (
      <CalendarShell title="Reservations">
        <ScreenError
          error={location.error}
          what="the service locations"
          onRetry={location.retry}
          retryMessage="The service locations couldn’t be loaded, so the calendar can’t be scoped. Try again."
        />
      </CalendarShell>
    );
  }

  return (
    <CalendarShell title="Reservations">
      <Typography
        data-testid="reservations-date-subheading"
        sx={{ fontSize: 15, fontWeight: 500, mb: 1 }}
      >
        {windowLabel(effectiveView, date)}
      </Typography>

      {/* One-row control bar (DNKADM-0300 A): date nav · segmented view/axis
          toggles · Filters(N) collapse · Show cancelled (OUTSIDE the collapse —
          a visibility toggle, not a filter: its state must stay visible) ·
          Book on behalf. Wraps on narrow widths rather than clipping. */}
      <Stack
        data-testid="reservations-control-bar"
        direction="row"
        sx={{ alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mb: 1.5 }}
      >
        <Button variant="outlined" color="inherit" onClick={goToday}>
          Today
        </Button>
        {/* Icon-only step buttons (DNKADM-0418): the tooltip and the accessible name
            are the same words, and they say what one step moves, by view. Plain
            `Button`s inside the `ButtonGroup` (not `IconAction`) so the group keeps
            its joined styling; `describeChild` mirrors `IconAction`. */}
        <ButtonGroup size="small" aria-label="Step date">
          <Tooltip title={`Previous ${stepUnit}`} describeChild>
            <Button
              aria-label={`Previous ${stepUnit}`}
              variant="outlined"
              color="inherit"
              onClick={goPrev}
              sx={{ minWidth: 44, px: 0 }}
            >
              <Icon name="chevron_left" />
            </Button>
          </Tooltip>
          <Tooltip title={`Next ${stepUnit}`} describeChild>
            <Button
              aria-label={`Next ${stepUnit}`}
              variant="outlined"
              color="inherit"
              onClick={goNext}
              sx={{ minWidth: 44, px: 0 }}
            >
              <Icon name="chevron_right" />
            </Button>
          </Tooltip>
        </ButtonGroup>

        {/* The date control (DNKADM-0393 U1, replacing the DNKADM-0300 A3 button +
            popover): the date IS a field — typing a date works — with a calendar
            icon button on its right. ONE click on that icon opens the native
            calendar, anchored directly under the field. There is no popover in
            between: the popover is what detached the browser's calendar to the far
            left of the window on desktop and iPad, because the input it held was
            itself inside a floating layer. Today + prev/next are unchanged. */}
        <TextField
          type="date"
          size="small"
          label="Date"
          value={dateDraft}
          onChange={(e) => onDateFieldChange(e.target.value)}
          // A field left cleared or half-typed shows the board's real date again.
          onBlur={() => setDateDraft(date)}
          inputRef={dateInputRef}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconAction
                    edge="end"
                    size="small"
                    label="Open calendar"
                    onClick={openDatePicker}
                  >
                    <Icon name="calendar_today" sx={{ fontSize: 18 }} />
                  </IconAction>
                </InputAdornment>
              ),
            },
          }}
          sx={{
            width: 190,
            // ONE calendar affordance, not two: WebKit/Blink draw their own
            // indicator at the right edge of a date input, which would sit beside
            // the button that is now the documented way to open the calendar.
            '& input::-webkit-calendar-picker-indicator': { display: 'none' },
          }}
        />

        {/* Top-level view (DNKADM-0271): the task-first board is the DEFAULT; the
            whole spatial machinery lives one toggle away under Grid. The three
            switchers are true segmented controls (DNKADM-0304 U8 — the
            approved board design's treatment). */}
        <SegmentedControl
          label="Reservations view"
          value={topView}
          options={[
            { value: 'nownext', label: 'Now & Next' },
            { value: 'grid', label: 'Grid' },
          ]}
          onChange={setTopView}
        />

        {topView === 'grid' && (
          <SegmentedControl
            label="Calendar view"
            value={view}
            options={[
              { value: 'day', label: 'Day' },
              { value: 'week', label: 'Week' },
            ]}
            onChange={setView}
          />
        )}

        {/* Resource-axis mode (DNKADM-0041): By Bay (default) / By Staff. A
            session-local re-grouping — switching never refetches. DAY only
            (DNKADM-0304 U7): Week is day-across × time-down with no lane per
            bay/coach, so the toggle could change nothing there — a control
            that cannot change anything is not offered (the DNKADM-0300
            principle). The chosen mode persists and reapplies on Day. */}
        {topView === 'grid' && view === 'day' && (
          <SegmentedControl
            label="Resource axis"
            value={axisMode}
            options={AXIS_MODES}
            onChange={setAxisMode}
          />
        )}

        {/* Filters(N) collapse (DNKADM-0300 A1): status / service / staff move
            into a popover; the active count stays visible on the control. The
            staff select is hidden entirely when no booking carries staff. */}
        {topView === 'grid' && (
          <>
            <Button
              variant="outlined"
              color={activeFilterCount > 0 ? 'primary' : 'inherit'}
              aria-haspopup="dialog"
              aria-expanded={filtersAnchor !== null}
              onClick={(e) => setFiltersAnchor(e.currentTarget)}
              startIcon={<Icon name="filter_list" sx={{ fontSize: 18 }} />}
            >
              {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Filters'}
            </Button>
            <Popover
              open={filtersAnchor !== null}
              anchorEl={filtersAnchor}
              onClose={() => setFiltersAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Stack sx={{ p: 2, gap: 1.5, width: 240 }}>
                {/* Also shown while a (linked) staff filter is set, so it can always be
                    cleared even on a window with no staff (DNKADM-0430). */}
                {(hasStaff || filterStaff !== ALL) && (
                  <TextField
                    select
                    size="small"
                    label="Staff"
                    value={filterStaff}
                    onChange={(e) => setFilterStaff(e.target.value)}
                  >
                    <MenuItem value={ALL}>All staff</MenuItem>
                    {windowStaff.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.label}
                      </MenuItem>
                    ))}
                    {staffFilterOutsideWindow && (
                      <MenuItem value={filterStaff}>Staff not in this window</MenuItem>
                    )}
                  </TextField>
                )}
                <TextField
                  select
                  size="small"
                  label="Service"
                  value={filterService}
                  onChange={(e) => setFilterService(e.target.value)}
                >
                  <MenuItem value={ALL}>All services</MenuItem>
                  {windowServices.map((s) => (
                    <MenuItem key={s.name} value={s.name}>
                      {s.name}
                    </MenuItem>
                  ))}
                  {serviceFilterOutsideWindow && (
                    <MenuItem value={filterService}>{filterService}</MenuItem>
                  )}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as StatusFilter)}
                >
                  <MenuItem value={ALL}>All statuses</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>
                {/* DNKADM-0408 U1 — stranded and needs-review bookings, via the server. */}
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={filterPaymentAttention}
                      onChange={(e) => setFilterPaymentAttention(e.target.checked)}
                    />
                  }
                  label="Payment needs attention"
                  sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                />
              </Stack>
            </Popover>

            {/* After hours (DNKADM-0300 E): the day's bookings outside the
                operating window — the load metric doesn't cover them, so this
                is where they stay visible. Day view only; omitted at zero. */}
            {view === 'day' && afterHours.length > 0 && (
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<Icon name="bedtime" sx={{ fontSize: 18 }} />}
                onClick={() => setAfterHoursOpen(true)}
              >
                {`After hours (${afterHours.length})`}
              </Button>
            )}

            {/* Cancelled-collapse (DNKADM-0271): a VISIBILITY toggle, kept
                outside the Filters collapse on purpose (DNKADM-0300 A4). */}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showCancelled}
                  onChange={(e) => setShowCancelled(e.target.checked)}
                  slotProps={{ input: { 'aria-label': 'Show cancelled' } }}
                />
              }
              label="Show cancelled"
              sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: 12.5 } }}
            />
          </>
        )}

        <Box sx={{ flex: 1, minWidth: 8 }} />

        {/* Block off (DNKADM-0373 U1) — one intent, in place of the resource
            schedules, the location hours and the staff schedules screens. SET UP,
            so ABSENT for a STAFF session (DNKADM-0375). */}
        {canBlockOff && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Icon name="event_busy" />}
            onClick={() => setBlockingOff(true)}
            disabled={!serviceLocationId}
          >
            Block off
          </Button>
        )}
        {/* Book on behalf (DNKADM-0061 U2) — enabled once a location resolves. Every
            operator, STAFF included (DNKADM-0375). */}
        <Button
          variant="contained"
          startIcon={<Icon name="add" />}
          onClick={() => setCreating({})}
          disabled={!serviceLocationId}
        >
          Book on behalf
        </Button>
      </Stack>

      {/* A closure cannot hide behind the axis toggle (DNKADM-0388 U2, DNKADM-0389 U1): on By
          Bay, name the coaches closed or partly closed on this date and offer By Staff; on By
          Staff, name the bays and offer By Bay. One line, two directions. */}
      {topView === 'grid' && view === 'day' && blockedLine.names.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 1.5 }}
          data-testid={blockedLine.testId}
          action={
            <Button color="inherit" size="small" onClick={() => setAxisMode(blockedLine.to)}>
              {blockedLine.action}
            </Button>
          }
        >
          {blockedLine.what} blocked {isToday ? 'today' : `on ${dateButtonLabel(date)}`}: {blockedLine.names.join(', ')}
        </Alert>
      )}

      {/* Service legend (the services present in the window with their derived colors)
          — a Grid concern; the board's cards carry their own service accents. */}
      {topView === 'grid' && windowServices.length > 0 && (
        <Stack
          direction="row"
          sx={{ alignItems: 'center', gap: 1.75, flexWrap: 'wrap', mb: 1.5 }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              color: t.textSubtle,
            }}
          >
            Service
          </Typography>
          {windowServices.map((s) => (
            <Stack key={s.name} direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
              <Box
                aria-hidden
                sx={{ width: 11, height: 11, borderRadius: '2px', bgcolor: s.color.main }}
              />
              <Typography sx={{ fontSize: 12, color: inkAlpha(theme, 0.7) }}>{s.name}</Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {/* View area — the Now & Next board (default) or the spatial grid. */}
      <Box sx={{ position: 'relative' }}>
        {resource.status === 'loading' && <CalendarLoading inline />}

        {resource.status === 'error' && (
          <ScreenError
            variant="panel"
            error={resource.error}
            what="the reservations"
            onRetry={resource.refetch}
            forbiddenMessage={roleLimitedMessage('This screen', 'OWNER or ADMIN')}
            retryMessage="The reservations could not be loaded. Please try again."
            preferServerMessage
            rejectedTitle={
              isInvalidWindowError(resource.error) ? 'Invalid date window' : 'Something went wrong'
            }
            rejectedMessage={
              isInvalidWindowError(resource.error) ? INVALID_WINDOW_MESSAGE : undefined
            }
            sx={{ mt: 1 }}
          />
        )}

        {/* The window read stops at its page cap (DNKADM-0431): say so rather than present a
            partial window as complete. */}
        {resource.status === 'ready' && resource.data?.truncated === true && (
          <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="calendar-truncated">
            {truncatedWindowMessage(all.length)}
          </Alert>
        )}

        {paymentAttention && (
          <PaymentAttentionList
            page={paymentAttentionAll.data}
            status={paymentAttentionAll.status}
            timeZone={venueTimeZone}
            onRetry={paymentAttentionAll.refetch}
            onSelect={setSelectedBooking}
          />
        )}

        {resource.status === 'ready' && topView === 'nownext' && (
          <NowNextView
            reservations={all}
            roster={roster}
            // The same active-staff rows the By-Staff axis renders — already
            // loaded by the shell; the coach-load chart must not refetch it
            // (DNKADM-0298 U1).
            staffRoster={staffRoster}
            operating={dayOperating}
            pendingTotal={pendingTotal}
            timeZone={venueTimeZone}
            unresolvedCandidates={unresolvedCandidates.data ?? []}
            onSelect={setSelectedBooking}
            onCheckIn={boardCheckIn}
            onNoShow={boardNoShow}
            onConfirm={confirmBooking}
            onDecline={boardDecline}
          />
        )}

        {resource.status === 'ready' &&
          topView === 'grid' &&
          (view === 'day' ? (
            // The responsive Day grid (DNKADM-0300) renders whenever any booking
            // matches OR a roster backs the axis (DNKADM-0040 — the lanes always
            // render, so an empty day shows every row to book into); with NO
            // roster + NO bookings it falls back to the shared empty card, which
            // distinguishes "nothing in this window" from "your filters hid
            // everything".
            gridReservations.length > 0 || hasAxisRoster ? (
              <DayGrid
                date={date}
                reservations={gridReservations}
                onSelect={setSelectedBooking}
                operating={dayOperating}
                axis={axis}
                axisLabel={axisLabel}
                timeZone={venueTimeZone}
                blocks={dayBlocks}
                onSlotClick={onSlotClick}
                onBlockSelect={onBlockSelect}
              />
            ) : (
              <CalendarEmpty hasWindowData={all.length > 0} />
            )
          ) : gridReservations.length > 0 || hasAxisRoster ? (
            <WeekGrid
              date={date}
              reservations={gridReservations}
              onSelect={setSelectedBooking}
              onOpenDay={openDay}
              operatingDays={operatingDays}
              axis={axis}
              timeZone={venueTimeZone}
              blocks={weekBlocks}
              onBlockSelect={onBlockSelect}
            />
          ) : (
            <CalendarEmpty hasWindowData={all.length > 0} />
          ))}
      </Box>

      {/* Detail drawer — opens on the selected booking; Reschedule / Cancel launch
          the write surfaces below. Closing clears the selection. */}
      <ReservationDrawer
        reservation={activeAction === null ? selectedBooking : null}
        onClose={closeDrawer}
        onReschedule={() => setActiveAction('reschedule')}
        onCancel={() => {
          setDeclineIntent(false);
          setActiveAction('cancel');
        }}
        onCheckIn={markCheckIn}
        onNoShow={markNoShowAction}
        onConfirm={confirmBooking}
        onReassign={() => setActiveAction('reassign')}
        onEditTime={() => setActiveAction('edit')}
        onChangeAssignment={() => setActiveAction('assignment')}
        onPendingChargeChanged={refreshPendingCharge}
        onTakePayment={() => setActiveAction('takepayment')}
        // Offered to the STAFF floor too (DNKADM-0408 U1): Check payment never charges,
        // and dink-api opens it to STAFF_ROLES.
        onPaymentChecked={onPaymentChecked}
        // Close out reflects exactly as Check payment does (replace, then refetch).
        {...(isAdmin ? { onPaymentClosedOut: onPaymentChecked } : {})}
      />

      {/* Reschedule (DNKADM-0061 U3) — the credit-neutral availability-picker dialog
          (PUT {uuid} with the new slot); success → toast + refetch. */}
      {selectedBooking && activeAction === 'reschedule' && (
        <RescheduleDialog
          open
          reservation={selectedBooking}
          onClose={() => setActiveAction(null)}
          onDone={onActionDone}
          timeZone={venueTimeZone}
        />
      )}

      {/* Cancel — the shared audited dialog (required reason + directive, the
          cancellation preview, admin-force-full-refund). DNKADM-0415: success closes
          the drawer and clears the selection, toasts, and refetches the window, from
          the Grid and Now & Next alike. The cancel response is `{ cancelled, refund,
          cardOnFileCharge }`, not a reservation, so it is never put in the drawer. A
          failure stays inline in the dialog and leaves the selection as it was. */}
      {selectedBooking && activeAction === 'cancel' && (
        <CancelReservationDialog
          open
          reservation={selectedBooking}
          asDecline={declineIntent}
          onClose={() => setActiveAction(null)}
          onDone={(message) => {
            // Cancelling a PENDING booking is a decline — it leaves the
            // tenant-wide queue, so refresh the shared count (DNKADM-0276).
            // Read the bucket BEFORE the success path clears the selection.
            const wasPending = deriveStatusBucket(selectedBooking.status) === 'pending';
            onActionDone(message);
            if (wasPending) notifyConfirmationQueueChanged();
          }}
        />
      )}

      {/* Check-in (DNKADM-0146) — the card-on-file confirm-charge dialog (amount
          override + charge/comp toggle). Non-card-on-file marks Showed with no dialog.
          Success → toast + refetch. */}
      {selectedBooking && activeAction === 'checkin' && (
        <CheckInDialog
          open
          reservation={selectedBooking}
          onClose={() => setActiveAction(null)}
          onDone={onActionDone}
        />
      )}

      {/* Checking in a booking that still owes money (DNKADM-0402 U5) — take the money
          now, or check them in and collect later. Nothing is charged automatically
          either way, and an unconfirmed payment is not payable from here at all. */}
      {selectedBooking && activeAction === 'checkin-unpaid' && (
        <UnpaidCheckInDialog
          open
          reservation={selectedBooking}
          onClose={() => setActiveAction(null)}
          // DNKADM-0489: Take payment from here finishes the check-in in the same flow.
          onTakePayment={() => setActiveAction('takepayment-checkin')}
          onCheckInAnyway={(managerPin) => {
            const uuid = selectedBooking.reservationUuid;
            setActiveAction(null);
            if (uuid) {
              // `charge: false` states the intent on the wire: this check-in takes no
              // money. The booking stays Unpaid until staff take payment. On a No-show
              // with its fee queued it also cancels that fee, so STAFF sends the manager
              // PIN (DNKADM-0475).
              void runDirectMark(
                checkInReservation(
                  uuid,
                  managerPin === undefined ? { charge: false } : { charge: false, managerPin },
                ),
                isPaymentInProgress(selectedBooking)
                  ? 'Checked in — the payment is still unconfirmed.'
                  : 'Checked in — still unpaid.',
              );
            }
          }}
        />
      )}

      {/* Take payment for ONE unpaid booking (DNKADM-0402 U5) — the only place this
          console charges one. The settled booking replaces the drawer's copy in place
          (the 0257 replace-then-refetch path), so the badge reads Paid at once. */}
      {/* Reachable ONLY from the two places that offer it — the drawer's Unpaid banner
          and the unpaid check-in prompt — neither of which offers it for a payment
          pay-api never confirmed (Operator decision, 2026-09-22). It deliberately stays
          mounted after an already-paid refusal, which is how the desk reads it. */}
      {/* DNKADM-0489: from the unpaid check-in prompt it also checks them in once the
          payment lands, and `onPaid` gets the re-read, checked-in booking; the same
          replace-then-refetch reloads the window and the payment-attention list, so the
          tile and the drawer show the cancelled no-show fee as it now is. */}
      {selectedBooking &&
        (activeAction === 'takepayment' || activeAction === 'takepayment-checkin') && (
        <TakePaymentDialog
          open
          reservation={selectedBooking}
          checkInAfterPayment={activeAction === 'takepayment-checkin'}
          onClose={() => setActiveAction(null)}
          onPaid={(updated, message) => onMutationReflected(updated, message)}
          onRefreshed={(updated) => {
            setSelectedBooking(updated);
            refetch();
          }}
        />
      )}

      {/* No-show (DNKADM-0188) — all reservations open the four-state dialog so credit
          bookings submit an explicit no-charge body and pre-0317 reads keep generic
          copy. Success → toast + refetch. */}
      {selectedBooking && activeAction === 'noshow' && (
        <NoShowDialog
          open
          reservation={selectedBooking}
          onClose={() => setActiveAction(null)}
          onDone={onActionDone}
        />
      )}

      {/* Reassign staff/bay (DNKADM-0211 U2) — the confirmed-booking, money-neutral
          assignment change (PUT {uuid}/assignment). The 200 replaces the booking in
          place (drawer stays open); a terminal/not-confirmed rejection refreshes the
          window so the calendar catches up. NOT reschedule, NOT the /details Edit. */}
      {selectedBooking && activeAction === 'reassign' && (
        <ReassignAssignmentDialog
          open
          reservation={selectedBooking}
          onClose={() => setActiveAction(null)}
          onDone={onReassigned}
          onStale={refetch}
        />
      )}

      {/* A confirm's refusal (DNKADM-0396; DNKAPI-0542 and DNKAPI-0636 re-check at confirm
          time): the dialog names every reason and the booking(s) in the way. "Change it"
          opens the pending-request assignment dialog below (the confirmed-booking
          reassign is `409 RESERVATION_NOT_CONFIRMED` for a pending row); "Book anyway"
          re-confirms with exactly the flags this refusal named; Cancel leaves the drawer
          as it was. */}
      {confirmConflict && (
        <OverrideConflictDialog
          open
          refusal={confirmConflict.view}
          onCancel={() => setConfirmConflict(null)}
          onChangeIt={() => {
            const { reservation } = confirmConflict;
            setConfirmConflict(null);
            setSelectedBooking(reservation);
            setActiveAction('assignment');
          }}
          onBookAnyway={() => {
            const { reservation, view, sent } = confirmConflict;
            setConfirmConflict(null);
            confirmBooking(reservation, { ...sent, ...overrideAcknowledgements(view) });
          }}
        />
      )}
      {selectedBooking && activeAction === 'assignment' && (
        <ChangeAssignmentDialog
          open
          reservation={selectedBooking}
          onClose={() => setActiveAction(null)}
          onDone={(message, updated) => {
            if (updated) onMutationReflected(updated, message);
            else onActionDone(message);
          }}
          onStale={refetch}
        />
      )}
      {/* Edit time on a pending booking (DNKADM-0393 U2) — the Pending tab's own
          dialog, mounted here unchanged. `onDone` takes the SAME replace-then-refetch
          path as every other write on this shell: the mutation's returned reservation
          replaces the drawer's copy in place (drawer stays open, new time showing)
          while the window refetches behind it. The timezone is the BOOKING's location,
          not the selected one — the same lookup the Pending tab makes. */}
      {selectedBooking && activeAction === 'edit' && (
        <EditReservationDetailsDialog
          open
          reservation={selectedBooking}
          onClose={() => setActiveAction(null)}
          onDone={(message, updated) => {
            if (updated) onMutationReflected(updated, message);
            else onActionDone(message);
          }}
          onStale={refetch}
          timeZone={resolveCalendarZone(
            location.locations.find((l) => l.id === selectedBooking.locationId)?.timezone,
            tenantZone,
          )}
        />
      )}

      {/* After-hours list (DNKADM-0300 E) — the shared bookings-list card; a
          row opens the same detail surface as a grid block. */}
      <BookingsListDialog
        open={afterHoursOpen}
        title="After hours"
        subtitle="Outside operating hours on this day"
        reservations={afterHours}
        timeZone={venueTimeZone}
        onClose={() => setAfterHoursOpen(false)}
        onSelect={(r) => {
          setAfterHoursOpen(false);
          setSelectedBooking(r);
        }}
      />

      {/* Block off (DNKADM-0373 U1) — per-bay / per-coach date exceptions at the
          active location, defaulting to the board's date. */}
      {canBlockOff && blockingOff && serviceLocationId && (
        <BlockOffDialog
          open
          locationId={serviceLocationId}
          timeZone={venueTimeZone}
          initialDate={date}
          onClose={() => setBlockingOff(false)}
          onChanged={refetchBlocks}
        />
      )}

      {/* Book on behalf (DNKADM-0061 U2) — create-on-behalf at the active location. */}
      {creating && serviceLocationId && (
        <CreateReservationDialog
          open
          locationId={serviceLocationId}
          onClose={() => setCreating(null)}
          onDone={onActionDone}
          timeZone={venueTimeZone}
          {...(creating.preset ? { preset: creating.preset } : {})}
        />
      )}

      {/* Blocked drawer (DNKADM-0389 U3) — the same shell and position as the booking
          drawer; Remove deletes the one exception and the board re-reads its blocks;
          Edit hands off to the Block off dialog on this date. */}
      <BlockedDrawer
        block={selectedBlock}
        canWrite={canBlockOff}
        onClose={() => setSelectedBlock(null)}
        onRemoved={refetchBlocks}
        onEdit={() => {
          setSelectedBlock(null);
          setBlockingOff(true);
        }}
      />
    </CalendarShell>
  );
}

// --- Sub-components -------------------------------------------------------------

/** The page chrome (title + content), shared across the gating + ready states. */
function CalendarShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box component="section">
      <Typography component="h1" sx={{ fontSize: 22, fontWeight: 500, mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

/** Loading state (full-screen by default; `inline` for the grid region). */
function CalendarLoading({ inline = false }: { inline?: boolean }) {
  const t = resolveTokens(useTheme());
  const body = (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, py: 3 }} role="status">
      <CircularProgress size={20} />
      <Typography sx={{ color: t.text2 }}>Loading reservations…</Typography>
    </Stack>
  );
  if (inline) return body;
  return <CalendarShell title="Reservations">{body}</CalendarShell>;
}

/** Empty state — distinguishes "filtered everything out" from "nothing in the window". */
function CalendarEmpty({ hasWindowData }: { hasWindowData: boolean }) {
  const t = resolveTokens(useTheme());
  return (
    <Box sx={{ textAlign: 'center', py: '52px', color: t.text2 }}>
      <Icon name="event_busy" sx={{ fontSize: 46, color: t.lineStrong }} />
      <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1, color: 'text.primary' }}>
        {hasWindowData ? 'No reservations match these filters' : 'No reservations in this window'}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.75, maxWidth: 360, mx: 'auto' }}>
        {hasWindowData
          ? 'Adjust the staff, service, or status filters to see more.'
          : 'Pick another day or week, or change the selected location.'}
      </Typography>
    </Box>
  );
}
